import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { switchCompany } from "@/components/CompanySwitcher";

export const err = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => `${(x.loc || []).slice(-1)[0] || "field"}: ${x.msg}`).join("; ");
  return typeof d === "string" ? d : "Something went wrong";
};
const L = "block text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1";
const STAFF_AREAS = ["pipeline", "applications", "contracts", "horse_ads", "review", "contacts", "settings"];
const GRANTS = { company_settings: "Company settings", contract_template: "Contract template", employees: "Employees", quickbooks: "QuickBooks" };
export const Card = ({ title, children, testid }) => (
  <section data-testid={testid} className="bg-white border rounded-xl p-4 sm:p-5 space-y-3">{title && <h3 className="font-semibold">{title}</h3>}{children}</section>
);

export function Fields({ list, f, set, c }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {list.map(([k, label]) => <div key={k}><label className={L}>{label}</label>
        <Input data-testid={`company-${k}`} disabled={k === "slug" && c.id === "bahs"} value={f[k] || ""} onChange={(e) => set({ [k]: e.target.value })} /></div>)}
    </div>
  );
}

export function OpenIn({ c, to, label, testid }) {
  const go = async () => { try { await switchCompany(c.id, to); } catch (e) { toast.error(err(e)); } };
  return <Button data-testid={testid} variant="outline" onClick={go}><ExternalLink className="w-4 h-4 mr-2" />{label}</Button>;
}

export function BrandingExtras({ c, f, set, onSaved }) {
  const logo = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/platform/companies/${c.id}/logo`, fd); toast.success("Logo uploaded"); onSaved(); } catch (e) { toast.error(err(e)); }
  };
  const color = (k, d) => <div><label className={L}>{k === "primary" ? "Main colour" : "Accent"}</label>
    <input data-testid={`company-color-${k}`} type="color" value={f.colors?.[k] || d} onChange={(e) => set({ colors: { ...f.colors, [k]: e.target.value } })} /></div>;
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <div><label className={L}>Logo</label>
        {c.logo_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${c.logo_url}`} alt="" className="h-12 mb-1" />}
        <input data-testid="company-logo-input" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={(e) => e.target.files[0] && logo(e.target.files[0])} /></div>
      <div className="flex gap-4">{color("primary", "#0B0C0E")}{color("accent", "#451A03")}</div>
      {[["ai_brand_voice", "AI brand voice"], ["ai_writing_style", "AI writing style"]].map(([k, l]) => <div key={k} className="sm:col-span-2"><label className={L}>{l}</label>
        <Textarea data-testid={`company-${k}`} value={f[k] || ""} onChange={(e) => set({ [k]: e.target.value })} /></div>)}
    </div>
  );
}

export function ContractLines({ c, f, set }) {
  return <div><label className={L}>Contract company lines (one per line; blank = built from the company details)</label>
    <Textarea data-testid="company-contract-lines" rows={5} disabled={c.id === "bahs"} placeholder={c.id === "bahs" ? "Bad Ass uses its original agreement wording" : ""}
      value={f.lines_text || ""} onChange={(e) => set({ lines_text: e.target.value })} /></div>;
}

export function Readiness({ c, onSaved }) {
  const [r, setR] = useState(null);
  const load = () => api.get(`/platform/companies/${c.id}/readiness`).then((x) => setR(x.data)).catch(() => {});
  useEffect(() => { load(); }, [c.id, c.contract_company_lines, c.legal_name, c.name, c.short_name, c.contact_email, c.contact_phone, c.contact_address, c.facebook_page_name, c.contract_template?.version, c.auction_close_time, c.auction_time_zone]); // eslint-disable-line react-hooks/exhaustive-deps
  const preview = async () => {
    try { const { data } = await api.get(`/platform/companies/${c.id}/contract-preview`, { responseType: "blob" }); window.open(URL.createObjectURL(data), "_blank", "noopener"); } catch (e) { toast.error(err(e)); }
  };
  const approve = async () => {
    if (!window.confirm("Approve this contract wording for sellers of this company?")) return;
    try { setR((await api.post(`/platform/companies/${c.id}/contract-approval`)).data); toast.success("Contract wording approved"); onSaved(); } catch (e) { toast.error(err(e)); }
  };
  if (!r) return null;
  const bahs = c.id === "bahs";
  const Row = ({ ok, label, testid }) => <p data-testid={testid} className={`text-sm font-medium ${ok ? "text-emerald-700" : "text-amber-700"}`}>{ok ? "✓" : "!"} {label}</p>;
  return (
    <div data-testid="company-readiness" className="space-y-2 border rounded-lg p-3 bg-stone-50">
      <Row testid="readiness-contract" ok={r.contract_approved} label={bahs ? "Uses the original Bad Ass agreement" : r.contract_approved ? `Contract wording approved by ${r.contract_approval?.by}` : !r.has_template ? "No contract wording yet — write and save it below, then approve" : "Contract wording not approved — contracts cannot be sent yet"} />
      {!bahs && <div className="flex flex-wrap gap-2">
        {r.has_template && <Button data-testid="contract-preview" variant="outline" onClick={preview}>Preview sample contract</Button>}
        {r.has_template && !r.contract_approved && <Button data-testid="contract-approve" onClick={approve}>Approve contract wording</Button>}
      </div>}
      <Row testid="readiness-privacy" ok={r.privacy_published} label={r.privacy_published ? "Privacy Policy published" : "Privacy Policy not published — the seller portal cannot be switched on yet"} />
      {!bahs && !r.privacy_published && <OpenIn c={c} to="/settings" label="Write & publish Privacy Policy" testid="company-open-legal" />}
    </div>
  );
}

const ZONES = ["America/Edmonton", "America/Regina", "America/Winnipeg", "America/Toronto", "America/Vancouver", "America/Halifax", "America/St_Johns",
  "America/Denver", "America/Phoenix", "America/Chicago", "America/New_York", "America/Los_Angeles"];
export function AuctionFields({ c, f, set }) {
  const bahs = c.id === "bahs";
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <div><label className={L}>Auction closing time</label>
        <Input data-testid="company-auction_close_time" type="time" disabled={bahs} value={bahs ? "20:00" : f.auction_close_time || "20:00"} onChange={(e) => set({ auction_close_time: e.target.value })} /></div>
      <div><label className={L}>Auction time zone</label>
        <select data-testid="company-auction_time_zone" disabled={bahs} value={bahs ? "America/Edmonton" : f.auction_time_zone || "America/Edmonton"} onChange={(e) => set({ auction_time_zone: e.target.value })} className="h-10 w-full border rounded-md px-2 bg-white disabled:opacity-50">
          {ZONES.map((z) => <option key={z} value={z}>{z.replace("America/", "").replace("_", " ")}</option>)}
        </select></div>
      {bahs && <p className="text-xs text-stone-500 sm:col-span-2">Bad Ass auctions always close at 8:00 PM Mountain Time.</p>}
    </div>
  );
}

export function ContractTemplateEditor({ c, onSaved }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ title: "", intro: "", text: "" });
  const touched = useRef(false);
  useEffect(() => {
    touched.current = false;
    api.get(`/platform/companies/${c.id}/contract-template`).then((r) => {
      setD(r.data);
      const t = r.data.template;
      if (!touched.current) setF(t ? { title: t.title, intro: t.intro, text: r.data.text } : { title: "", intro: "", text: "" });
    }).catch(() => {});
  }, [c.id]);
  const edit = (p) => { touched.current = true; setF((x) => ({ ...x, ...p })); };
  if (c.id === "bahs" || !d) return null;
  const starter = () => { if (!f.text || window.confirm("Replace the text below with the shared starting wording?")) edit({ ...d.starter }); };
  const save = async () => {
    try { const r = await api.put(`/platform/companies/${c.id}/contract-template`, f); setD(r.data); toast.success(`Contract wording saved (version ${r.data.template.version}) — review and approve it before sending`); onSaved(); } catch (e) { toast.error(err(e)); }
  };
  return (
    <div data-testid="contract-template-editor" className="space-y-2">
      <p className="text-sm text-stone-600">This company's complete agreement wording. Each saved version must be approved; signed agreements keep the exact wording the seller accepted.
        Include <code>{d.tokens.fee}</code> and <code>{d.tokens.listing}</code> where the fee amounts are printed. Start each clause on its own line as “1. Heading”.</p>
      <p data-testid="contract-template-legal-note" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">The shared starting wording comes from the Bad Ass agreement and includes Alberta law, GST and other terms. Have this company's own legal adviser review every clause before approving.</p>
      {d.template && <p data-testid="contract-template-version" className="text-xs text-stone-500">Version {d.template.version} · saved by {d.template.updated_by}</p>}
      <div><label className={L}>Agreement title</label><Input data-testid="contract-template-title" value={f.title} onChange={(e) => edit({ title: e.target.value })} /></div>
      <div><label className={L}>Opening sentence</label><Input data-testid="contract-template-intro" value={f.intro} onChange={(e) => edit({ intro: e.target.value })} /></div>
      <div><label className={L}>Clauses</label><Textarea data-testid="contract-template-text" rows={18} className="font-mono text-xs" value={f.text} onChange={(e) => edit({ text: e.target.value })} /></div>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="contract-template-starter" variant="outline" onClick={starter}>Start from shared wording</Button>
        <Button data-testid="contract-template-save" onClick={save}>Save contract wording</Button>
      </div>
    </div>
  );
}

export function useMembers(cid) {
  const [rows, setRows] = useState([]);
  const load = () => api.get(`/platform/companies/${cid}/members`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async (m, quiet) => {
    try { await api.post(`/platform/companies/${cid}/members`, m); await load(); if (!quiet) toast.success("Saved"); return true; } catch (e) { toast.error(err(e)); return false; }
  };
  return { rows, load, save };
}

export function Users({ cid, m }) {
  const [f, setF] = useState({ email: "", role: "admin" });
  const add = async () => { if (await m.save({ ...f, grants: f.role === "admin" ? ["company_settings"] : [] })) setF({ email: "", role: "admin" }); };
  const del = async (uid) => { try { await api.delete(`/platform/companies/${cid}/members/${uid}`); m.load(); } catch (e) { toast.error(err(e)); } };
  return (
    <Card title="Administrators & staff" testid="company-members">
      {!m.rows.length && <p className="text-sm text-stone-500" data-testid="members-empty">No one assigned yet.</p>}
      {m.rows.map((r) => <div key={r.user_id} className="flex items-center gap-2 text-sm border-b last:border-0 py-1.5"><span className="flex-1 break-all">{r.email}</span>
        <span className="text-xs uppercase tracking-wider text-stone-500">{r.role}</span>
        <Button data-testid={`member-remove-${r.email}`} size="sm" variant="outline" onClick={() => del(r.user_id)}>Remove</Button></div>)}
      <div className="flex flex-wrap gap-2 pt-1">
        <Input data-testid="member-email" placeholder="email@company.com" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="flex-1 min-w-[200px]" />
        <select data-testid="member-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="h-10 border rounded-md px-2"><option value="admin">Admin</option><option value="staff">Staff</option></select>
        <Button data-testid="member-add" onClick={add}>Add</Button>
      </div>
    </Card>
  );
}

function PermRow({ r, m }) {
  const opts = r.role === "admin" ? Object.keys(GRANTS) : STAFF_AREAS;
  const cur = new Set(r.role === "admin" ? r.grants || [] : r.permissions || []);
  const toggle = (k) => {
    const next = cur.has(k) ? [...cur].filter((x) => x !== k) : [...cur, k];
    m.save({ email: r.email, role: r.role, permissions: r.role === "staff" ? next : [], grants: r.role === "admin" ? next : [] }, true);
  };
  return (
    <div data-testid={`perm-row-${r.email}`} className="border rounded-lg p-3">
      <p className="text-sm font-medium break-all">{r.email} <span className="text-xs uppercase text-stone-500">· {r.role}</span></p>
      <p className="text-xs text-stone-500 mb-2">{r.role === "admin" ? "Admins reach every area; tick the extra powers they hold." : "Staff only see the areas ticked here."}</p>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {opts.map((k) => <label key={k} className="flex items-center gap-1.5 text-sm"><input type="checkbox" data-testid={`perm-${r.email}-${k}`} checked={cur.has(k)} onChange={() => toggle(k)} />{GRANTS[k] || k.replace("_", " ")}</label>)}
      </div>
    </div>
  );
}

export function Permissions({ m }) {
  return <Card title="Permissions" testid="company-permissions">
    {!m.rows.length && <p className="text-sm text-stone-500">Add administrators or staff in the Users tab first.</p>}
    {m.rows.map((r) => <PermRow key={r.user_id} r={r} m={m} />)}
  </Card>;
}

export function Keys({ cid }) {
  const [rows, setRows] = useState([]);
  const [shown, setShown] = useState(null);
  const load = () => api.get(`/platform/companies/${cid}/api-keys`).then((r) => setRows(r.data));
  useEffect(() => { load(); setShown(null); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const issue = async (kind) => { try { const { data } = await api.post(`/platform/companies/${cid}/api-keys`, { kind }); setShown(data); load(); } catch (e) { toast.error(err(e)); } };
  const revoke = async (id) => { if (!window.confirm("Revoke this key? The website using it stops working.")) return; await api.delete(`/platform/companies/${cid}/api-keys/${id}`); load(); };
  return (
    <Card title="Website API credentials" testid="company-keys">
      {cid === "bahs" && <p className="text-xs text-stone-600">Bad Ass keeps its existing website keys (Settings). Keys added here are extra.</p>}
      {rows.map((k) => <div key={k.id} className="flex items-center gap-2 text-sm"><span className="flex-1">{k.kind} · …{k.last4} · {k.revoked_at ? "revoked" : "active"}</span>
        {!k.revoked_at && <Button size="sm" variant="outline" data-testid={`key-revoke-${k.last4}`} onClick={() => revoke(k.id)}>Revoke</Button>}</div>)}
      {shown && <p data-testid="key-shown" className="text-xs bg-amber-50 border border-amber-200 rounded p-2 break-all">Copy now — shown once: <b>{shown.key}</b></p>}
      <div className="flex gap-2"><Button size="sm" data-testid="key-issue-feed" onClick={() => issue("feed")}>New feed key</Button><Button size="sm" variant="outline" data-testid="key-issue-newsletter" onClick={() => issue("newsletter")}>New newsletter key</Button></div>
    </Card>
  );
}
